import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, BRL, formatDateBR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import MoneyInput from "@/components/MoneyInput";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Loader2,
  Receipt,
  CheckCircle2,
  Clock,
  AlertTriangle,
  CalendarDays,
  Undo2,
  FileText,
} from "lucide-react";

const TODAY = new Date().toISOString().slice(0, 10);

function monthLabel(ym) {
  if (!ym) return "—";
  const [y, m] = ym.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
}

function paymentMonthLabel(competencia_ym, payment_ym) {
  // Row title uses the PAYMENT month (what the user "thinks" the DAS is called),
  // while a smaller caption shows the competência (notes considered).
  const payLabel = monthLabel(payment_ym);
  const compLabel = monthLabel(competencia_ym);
  return { payLabel, compLabel };
}

export default function FinanceImpostos() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  // Pay dialog
  const [payOpen, setPayOpen] = useState(false);
  const [payTarget, setPayTarget] = useState(null); // the month row
  const [payDate, setPayDate] = useState(TODAY);
  const [payAmount, setPayAmount] = useState("");
  const [payNote, setPayNote] = useState("");
  const [debitCaixa, setDebitCaixa] = useState(false);
  const [paying, setPaying] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/finance/impostos");
      setData(r.data || null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openPay = (row) => {
    setPayTarget(row);
    setPayDate(TODAY);
    setPayAmount(String(row.simples_total || ""));
    setPayNote("");
    setDebitCaixa(false);
    setPayOpen(true);
  };

  const confirmPay = async () => {
    if (!payTarget) return;
    const amt = Number(payAmount);
    if (!amt || amt <= 0) {
      toast.error("Informe um valor válido");
      return;
    }
    setPaying(true);
    try {
      await api.post(`/finance/impostos/${payTarget.month}/pay`, {
        paid_date: payDate,
        amount: amt,
        note: payNote,
        debit_caixa: debitCaixa,
      });
      toast.success(debitCaixa ? "DAS pago — valor debitado do caixa" : "DAS marcado como pago");
      setPayOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao registrar pagamento");
    } finally {
      setPaying(false);
    }
  };

  const undoPay = async (month) => {
    if (!window.confirm("Desfazer o pagamento deste mês?")) return;
    try {
      await api.delete(`/finance/impostos/${month}/pay`);
      toast.success("Pagamento desfeito");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao desfazer");
    }
  };

  if (loading && !data) return (
    <div className="flex items-center justify-center py-16 text-slate-500">
      <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Carregando…
    </div>
  );

  const months = data?.months || [];
  const pending = months.filter((m) => !m.paid);
  const paid    = months.filter((m) => m.paid);
  const overdueCount = pending.filter((m) => m.is_overdue).length;

  return (
    <div className="space-y-5 max-w-5xl pb-16" data-testid="finance-impostos-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-rose-600 flex items-center justify-center">
          <Receipt className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-xl sm:text-2xl font-black text-slate-900">
            Impostos — Simples Nacional (DAS)
          </h1>
          <p className="text-xs sm:text-sm text-slate-500">
            Valor do Simples acumulado por mês. Vencimento: dia 20 do mês seguinte.
          </p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="rounded-2xl bg-gradient-to-br from-rose-500 to-rose-600 text-white p-4" data-testid="impostos-pending">
          <p className="text-[10px] font-black uppercase tracking-widest opacity-80">
            <Clock className="w-3 h-3 inline mr-0.5" /> A pagar
          </p>
          <p className="mt-2 text-xl sm:text-2xl font-black tabular-nums">{BRL(data?.total_pending || 0)}</p>
          <p className="text-[10px] opacity-80 mt-0.5">
            {pending.length} mês(es) pendente(s)
            {overdueCount > 0 && (
              <span className="ml-1 bg-white/20 px-1.5 py-0.5 rounded font-black">
                {overdueCount} vencido(s)
              </span>
            )}
          </p>
        </div>
        <div className="rounded-2xl bg-white border border-emerald-200 p-4">
          <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700">
            <CheckCircle2 className="w-3 h-3 inline mr-0.5" /> Já pago
          </p>
          <p className="mt-2 text-xl font-black tabular-nums text-emerald-700">{BRL(data?.total_paid || 0)}</p>
          <p className="text-[10px] text-slate-500 mt-0.5">{paid.length} mês(es) quitado(s)</p>
        </div>
        <div className="rounded-2xl bg-white border border-sky-200 p-4 col-span-2 sm:col-span-1">
          <p className="text-[10px] font-black uppercase tracking-widest text-sky-700">
            <CalendarDays className="w-3 h-3 inline mr-0.5" /> Mês atual
          </p>
          <p className="mt-2 text-xl font-black tabular-nums text-sky-700">{BRL(data?.this_month || 0)}</p>
          <p className="text-[10px] text-slate-500 mt-0.5">{data?.today_month || "—"}</p>
        </div>
      </div>

      {/* Pending months */}
      <section className="bg-white rounded-2xl border border-slate-200 p-5 card-shadow">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-rose-600" />
          <h2 className="font-heading text-base font-black text-slate-900">Pendentes</h2>
          <span className="text-[11px] text-slate-500">({pending.length})</span>
        </div>
        {pending.length === 0 ? (
          <p className="text-sm text-slate-500 italic">Nenhum mês com Simples pendente. 🎉</p>
        ) : (
          <ul className="space-y-2">
            {pending.map((m) => {
              const { payLabel, compLabel } = paymentMonthLabel(m.month, m.payment_month);
              const canPay = TODAY >= m.due_date;
              return (
              <li
                key={m.month}
                className={`flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-xl border ${
                  m.is_overdue ? "border-rose-300 bg-rose-50" : "border-slate-200 bg-slate-50"
                }`}
                data-testid={`impostos-month-${m.month}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-black text-slate-900 capitalize">DAS de {payLabel}</p>
                    {m.manual && (
                      <span className="inline-flex items-center gap-1 bg-sky-100 text-sky-700 text-[10px] px-1.5 py-0.5 rounded font-black">
                        LANÇ. MANUAL
                      </span>
                    )}
                    {m.is_overdue && (
                      <span className="inline-flex items-center gap-1 bg-rose-600 text-white text-[10px] px-1.5 py-0.5 rounded font-black">
                        <AlertTriangle className="w-3 h-3" /> VENCIDO
                      </span>
                    )}
                    {!canPay && (
                      <span className="inline-flex items-center gap-1 bg-slate-200 text-slate-600 text-[10px] px-1.5 py-0.5 rounded font-black">
                        <Clock className="w-3 h-3" /> AGUARDANDO VENCIMENTO
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500 capitalize">
                    Competência {compLabel} • {m.notas_count} nota(s) • Vence em {formatDateBR(m.due_date)}
                  </p>
                </div>
                <div className="flex items-center justify-between gap-3 sm:justify-end">
                  <div className="text-right">
                    <p className="text-base font-black tabular-nums text-rose-700">{BRL(m.simples_total)}</p>
                    <p className="text-[10px] text-slate-500">Base: {BRL(m.gross_total)}</p>
                  </div>
                  <Button
                    onClick={() => canPay && openPay(m)}
                    disabled={!canPay}
                    title={canPay ? "" : `Disponível a partir de ${formatDateBR(m.due_date)}`}
                    data-testid={`btn-pay-${m.month}`}
                    className="h-9 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-xl text-xs font-black"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    Dar baixa
                  </Button>
                </div>
              </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Paid months */}
      <section className="bg-white rounded-2xl border border-slate-200 p-5 card-shadow">
        <div className="flex items-center gap-2 mb-3">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          <h2 className="font-heading text-base font-black text-slate-900">Pagos</h2>
          <span className="text-[11px] text-slate-500">({paid.length})</span>
        </div>
        {paid.length === 0 ? (
          <p className="text-sm text-slate-500 italic">Nenhum pagamento registrado ainda.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {paid.map((m) => {
              const { payLabel, compLabel } = paymentMonthLabel(m.month, m.payment_month);
              return (
              <li
                key={m.month}
                className="flex flex-col sm:flex-row sm:items-center gap-2 py-3"
                data-testid={`impostos-paid-${m.month}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-black text-slate-900 capitalize">DAS de {payLabel}</p>
                  <p className="text-[11px] text-slate-500 capitalize">
                    Competência {compLabel} • Pago em {formatDateBR(m.paid_date)} • {m.notas_count} nota(s)
                    {m.note ? ` • "${m.note}"` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-3 justify-between sm:justify-end">
                  <div className="text-right">
                    <p className="text-sm font-black tabular-nums text-emerald-700">{BRL(m.paid_amount || m.simples_total)}</p>
                    {m.paid_amount && Math.abs(m.paid_amount - m.simples_total) > 0.01 && (
                      <p className="text-[10px] text-slate-400">Acum.: {BRL(m.simples_total)}</p>
                    )}
                  </div>
                  <Button
                    onClick={() => undoPay(m.month)}
                    variant="ghost"
                    data-testid={`btn-undo-${m.month}`}
                    className="h-8 px-2 text-slate-500 hover:text-rose-600"
                  >
                    <Undo2 className="w-4 h-4" />
                  </Button>
                </div>
              </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Pay dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-rose-600" />
              Dar baixa DAS
            </DialogTitle>
          </DialogHeader>
          {payTarget && (
            <div className="space-y-4">
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
                <p className="text-[11px] text-slate-500 uppercase font-black tracking-widest">Competência</p>
                <p className="text-base font-black text-slate-900 capitalize">{monthLabel(payTarget.month)}</p>
                <p className="text-[11px] text-slate-500 mt-0.5 capitalize">
                  DAS pago em: {monthLabel(payTarget.payment_month)}
                </p>
                <p className="text-[11px] text-slate-500 mt-1">
                  Simples acumulado: <span className="font-black text-rose-700">{BRL(payTarget.simples_total)}</span>
                </p>
                <p className="text-[11px] text-slate-500">
                  Vencimento: {formatDateBR(payTarget.due_date)}
                </p>
              </div>
              <div>
                <label className="text-[11px] font-black uppercase tracking-widest text-slate-600">Data do pagamento</label>
                <Input
                  type="date"
                  value={payDate}
                  onChange={(e) => setPayDate(e.target.value)}
                  data-testid="input-pay-date"
                  className="mt-1 h-10 rounded-xl"
                />
              </div>
              <div>
                <label className="text-[11px] font-black uppercase tracking-widest text-slate-600">Valor pago (R$)</label>
                <MoneyInput
                  value={payAmount}
                  onChange={setPayAmount}
                  data-testid="input-pay-amount"
                  className="mt-1 h-10 rounded-xl"
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  Pode ajustar se a guia veio com juros/multa.
                </p>
              </div>
              <div>
                <label className="text-[11px] font-black uppercase tracking-widest text-slate-600">Observação (opcional)</label>
                <Textarea
                  value={payNote}
                  onChange={(e) => setPayNote(e.target.value)}
                  placeholder="Nº da guia, forma de pagamento, etc."
                  data-testid="input-pay-note"
                  className="mt-1 rounded-xl text-sm"
                  rows={2}
                />
              </div>
              <label className="flex items-start gap-2 cursor-pointer p-3 rounded-xl border border-sky-200 bg-sky-50">
                <Checkbox
                  checked={debitCaixa}
                  onCheckedChange={(v) => setDebitCaixa(Boolean(v))}
                  data-testid="chk-debit-caixa"
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <p className="text-xs font-black text-sky-900">Debitar do Caixa da empresa</p>
                  <p className="text-[10.5px] text-sky-700 leading-snug mt-0.5">
                    Cria automaticamente uma saída na categoria "impostos", descontando este valor do saldo do caixa.
                  </p>
                </div>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)} className="rounded-xl">
              Cancelar
            </Button>
            <Button
              onClick={confirmPay}
              disabled={paying}
              data-testid="btn-confirm-pay"
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-black"
            >
              {paying ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              Confirmar pagamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
